
import Vue from 'vue';
import Component from 'vue-class-component';
import MainLayout from '../components/MainLayout.vue';
import SessionModel, { SessionData } from '../model/Session';
import api from '../service/api';
import JustifiedLayout from 'vue-justified-layout';
import Debounce from '@/decorators/debounce';
import { Watch, Prop } from 'vue-property-decorator';
import { getModule } from 'vuex-module-decorators';
import SessionsState, { SessionErrorCode } from '@/store/modules/Sessions';
import PhotosState from '@/store/modules/Photos';
import store from '@/store';
import Photo, { isPhoto } from '@/model/Photo';
import PhotoComponent from '@/components/session/Photo.vue';
import DragSelection from '@/components/session/DragSelection.vue';
import { cloneDeep as clone, replace } from 'lodash';
import { polishPlurals } from 'polish-plurals';
import { groupOperationBus } from '../components/GroupOperations.vue';
import SessionSettings from '../components/session/SessionSettings.vue';
import naturalCompare from 'natural-compare-lite';
import UploadModule from '@/store/modules/Upload';
import Placeholder, { isPlaceholder } from '@/model/Placeholder';
import { replaceBus } from '@/components/ReplaceDialog.vue';
import ConfirmationModel from '@/components/ConfirmationModel';
import ConfirmFinalize from '@/components/session/ConfirmFinalize.vue';
import NotFound from './NotFound.vue';
import UserState from '@/store/modules/User';
import { INTERFACE_SOUND_VOLUME, createInterfaceSound, playInterfaceSound } from '@/service/interfaceSound';

const sessionsState = getModule(SessionsState, store);
const photosState = getModule(PhotosState, store);
const uploadState = getModule(UploadModule, store);
const userState = getModule(UserState, store);
const PHOTO_DESELECT_SOUND_VOLUME = INTERFACE_SOUND_VOLUME * 0.3;
const initialSessionData: Nullable<Partial<SessionData>> = {
    name: null,
};

type Element = Photo|Placeholder;
const isSame = (p1: Element, p2: Element) => 
    (isPhoto(p1) && isPhoto(p2) && p1.id === p2.id) ||
    (isPlaceholder(p1) && isPlaceholder(p2) && p1.file === p2.file);

@Component({
    components: {
        MainLayout,
        JustifiedLayout,
        Photo: PhotoComponent,
        DragSelection,
        SessionSettings,
        ConfirmFinalize,
        NotFound,
    },
    async beforeRouteEnter(to, from, next) {
        if (!to.params.id) {
            sessionsState.setCreatingSession(true);
        } else {
            await sessionsState.editSession(to.params.id);
        }
        next();
    },
    beforeRouteLeave(to, from, next) {
        const vm = this as Session;
        const leavingToSessionList = to?.name === 'sessionList';

        if (!leavingToSessionList || vm.isLeavingSessionView) {
            sessionsState.setCreatingSession(false);
            next();
            return;
        }

        vm.isLeavingSessionView = true;
        vm.isSessionPhotosVisible = false;
        window.dispatchEvent(new CustomEvent('raws-session-leave'));
        window.setTimeout(() => {
            sessionsState.setCreatingSession(false);
            next();
        }, 320);
    },
})
export default class Session extends Vue {

    get sessionNotFound() {
        return sessionsState.sessionErrors[this.id!] === SessionErrorCode.SESSION_NOT_FOUND;
    }

    get finalizationMode() {
        return this.session?.finalizationMode;
    }

    get session() {
        if (this.id) {
            return sessionsState.sessions[this.id];
        }
    }

    get sessionShared() {
        const sessionLists = Object.values(sessionsState.sessionList);
        for (const sessionList of sessionLists) {
            const sessionListElement = sessionList.find(({ id }) => id === this.id);
            if (sessionListElement) {
                return sessionListElement.shared;
            }
        }

        return false;
    }

    get allPhotos() {
        const statePhotos = photosState.sessionPhotos[this.id!] || [];
        const originalPhoto = (id: string|undefined) => {
            if (!id) {
                return null;
            }

            const photo = statePhotos.find((p) => isPhoto(p) && p.id === id);
            return photo;
        };

        const uploadPhotos = (this.id && uploadState.placeholders[this.id] || []);
        const uploadPhotosWithoutId = uploadPhotos.filter(({ id }) => !id);
        const uploadPhotosWithId = uploadPhotos.filter(({ id }) => id).map(
            (p) => ({
                ...p,
                id: p.id!,
                orderedBy: originalPhoto(p.overwriteId)?.orderedBy || [],
                recommended: originalPhoto(p.overwriteId)?.recommended || false,
                finalized: originalPhoto(p.overwriteId)?.finalized || false,
                surcharge: originalPhoto(p.overwriteId)?.surcharge ?? null,
                saleMode: originalPhoto(p.overwriteId)?.saleMode || 'all',
                likedBy: originalPhoto(p.overwriteId)?.likedBy || [],
            }),
        );
        const processedUploadPhotos = [...uploadPhotosWithoutId, ...uploadPhotosWithId];
        const sessionPhotos = (
            this.id && photosState.sessionPhotos[this.id] || []
        ).filter(({ id }) => !processedUploadPhotos.some(({ overwriteId }) => overwriteId === id));
        

        return [...sessionPhotos, ...processedUploadPhotos];
    }

    get photos() {
        const filename = (p: Element) => (
                (isPlaceholder(p) && p.overwriteId && this.originalFilenames[p.overwriteId]) || 
                (isPhoto(p) && this.originalFilenames[p.id]) ||
                p.filename
            ).toLowerCase();

        const sort = {
            name: {
                asc: (a: Element, b: Element) => naturalCompare(filename(a), filename(b)),
                desc: (a: Element, b: Element) => naturalCompare(filename(b), filename(a)),
            }
        };

        return this.allPhotos.filter(photo => {
                return (
                    (!this.likedOnly && !this.orderedOnly && !this.session?.finalizationMode) ||
                    (
                        isPhoto(photo) &&
                        (
                            (this.likedOnly && photo.likedBy.length > 0) ||
                            ((this.orderedOnly || this.session?.finalizationMode) && photo.orderedBy.length > 0)
                        )
                    )
                );
            })
            .sort(
                sort[this.sort.field][this.sort.direction]
            );
    }

    get largeThumbs() {
        return this.photos.map(({ largeThumbUrl }) => largeThumbUrl);
    }

    get allPhotosFinalized() {
        return !!this.id &&
            (photosState.sessionPhotos[this.id] || []).every((photo) =>
                !photo.orderedBy || photo.orderedBy.length === 0 || photo.finalized
            );
    }

    get arePhotosOrdered() {
        return !!this.id &&
            (photosState.sessionPhotos[this.id] || []).some((photo) =>
                photo.orderedBy && photo.orderedBy.length > 0
            );
    }

    get shouldShowFinalization() {
        return !!this.id &&
            this.session &&
            this.session.finalizationEnabled &&
            !this.session.finalized &&
            this.arePhotosOrdered;
    }

    get displayablePhotos() {
        return this.photos.map((photo) => {
            return {
                ...photo,
                url: photo.smallThumbUrl || (isPlaceholder(photo) ? photo.largeThumbUrl : undefined),
                ...(photo.smallThumbDimensions || { width: 500, height: 500 }),
            };
        });
    }

    get justifiedLayoutOptions() {
        return {
            targetRowHeight: 214,
            containerWidth: this.justifiedLayoutWidth,
        };
    }
    public finalizationConfirmation: ConfirmationModel = new ConfirmationModel();
    public conflictMode = false;

    private sessionData: Nullable<Partial<SessionData>> = initialSessionData;
    private polishPlurals = polishPlurals;
    private lightboxOpen: boolean = false;
    private lightboxIndex: number = 0;
    private lightboxRecommendationInProgress = false;
    private lightboxSlideAnimating = false;
    private lightboxSlideDirection: 'next' | 'prev' = 'next';
    private lightboxSlideFromSrc = '';
    private lightboxSlideToSrc = '';
    private lightboxSlideToRecommended = false;
    private lightboxSlideTimeout?: number;
    private lightboxSlideToken = 0;
    private lightboxSlideReadyDeadline = 0;
    private lightboxSlideSettling = false;
    private lightboxSlideFinalStyle: { [property: string]: string } = {};
    private selectedPhotos: Element[] = [];
    private shiftAnchor: Element|null = null;
    private lastShiftSelection: Element[] = [];
    private originalFilenames: { [id: string]: string } = {};
    private isSame = isSame;
    private orderedOnly = false;
    private likedOnly = false;
    private photosLoading = false;
    private photosLoadFailed = false;
    private revealedThrough = -1;
    private readyThumbs = new Set<number>();
    private revealTimer?: number;
    private lockShortcuts = false;
    private sort: { field: 'name', direction: 'asc'|'desc' } = {
        field: 'name',
        direction: 'asc',
    };

    @Prop({
        type: String,
        required: false,
        default: null,
    })
    private id?: string;

    private resizeListener?: EventListener;
    private lightboxWheelLockListener?: (event: WheelEvent) => void;
    private unsubscribeStoreAction?: () => void;

    private justifiedLayoutWidth: number = 0;
    private shortcutListener?: (e: KeyboardEvent) => void;

    private dragSelectionStarted = false;
    private photosSelectedByDrag: Photo[] = [];
    private photoSelectSound = createInterfaceSound('/sound/pop.wav');
    private photoDeselectSound = createInterfaceSound('/sound/popout.wav', PHOTO_DESELECT_SOUND_VOLUME);
    private isLeavingSessionView = false;
    private isSessionPhotosVisible = false;
    private onDeletePhotos = () => this.clearSelection();
    private onRequestConflictOverride = () => { this.conflictMode = true; };
    private onResolveConflictOverride = () => { this.conflictMode = false; };

    async toggleFinalizationMode() {
        if (this.session?.finalizationMode) {
            await sessionsState.disableFinalizationMode(this.id!);
        } else {
            await sessionsState.enableFinalizationMode(this.id!);
        }
    }

    async finalize() {
        if (!this.allPhotosFinalized || !this.shouldShowFinalization)
            return;

        if (
            await this.finalizationConfirmation.confirm('Potwierdź finalizację sesji')
        ) {
            await sessionsState.finalizeSession(this.id!);
        }
    }

    @Watch('sort', {
        deep: true
    })
    resetSort() {
        this.originalFilenames = {};
    }

    toggleFinalization(photoId: string) {
        if (!this.session?.finalizationEnabled) return;

        const photos = this.photos.filter(isPhoto);
        if (photos.some((p) => p.id === photoId && p.finalized)) {
            photosState.unfinalize(photoId);
        } else if (photos.some((p) => p.id === photoId)) {
            photosState.finalize(photoId);
        }
    }

    toggleOrderedOnly() {
        this.clearSelection();
        this.likedOnly = false;
        this.orderedOnly = !this.orderedOnly;
    }

    toggleLikedOnly() {
        this.clearSelection();
        this.orderedOnly = false;
        this.likedOnly = !this.likedOnly;
    }

    public filesDropped() {
        if (!this.id) {
            this.sessionData!.name = 'Sesja bez nazwy';
        }
    }

    public openLightbox(index: number) {
        this.lightboxIndex = index;
        this.lightboxOpen = true;
        this.syncLightboxBodyClasses();
    }

    public photoSelectionChanged(photo: Element, isSelected: boolean, event?: MouseEvent) {
        let shouldPlayPopSound = false;

        if (event && event.shiftKey && this.shiftAnchor) {
            const shiftAnchorIndex = this.findPhotoIndex(this.shiftAnchor);
            const photoIndex = this.findPhotoIndex(photo);
            const fromIndex = Math.min(shiftAnchorIndex, photoIndex);
            const toIndex = Math.max(shiftAnchorIndex, photoIndex);

            this.selectedPhotos = this.selectedPhotos.filter(
                (p) => !this.lastShiftSelection.some((_p) => isSame(p, _p)),
            );
            this.lastShiftSelection = this.photos
                .filter((_, index) => {
                    return index >= fromIndex && index <= toIndex;
                })
                .filter((photoToSelect) => !this.isSelected(photoToSelect) && !isPlaceholder(photoToSelect));

            this.selectedPhotos = [...this.selectedPhotos, ...this.lastShiftSelection];
            shouldPlayPopSound = this.lastShiftSelection.length > 0;
        } else if (this.selectedPhotos.some((p) => isSame(p, photo)) && !isSelected) {
            this.selectedPhotos = this.selectedPhotos.filter((p) => !isSame(p, photo));
            if (this.shiftAnchor && isSame(this.shiftAnchor, photo)) {
                this.shiftAnchor = null;
            }
            this.playPhotoDeselectSound();
        } else if (!(this.selectedPhotos.includes(photo as Photo)) && isSelected) {
            this.selectedPhotos.push(photo as Photo);
            this.shiftAnchor = photo as Photo;
            shouldPlayPopSound = true;
        }

        if (shouldPlayPopSound) {
            this.playPhotoSelectSound();
        }
    }

    public handleDragSelection(photosInRectangle: { photo: any, index: number }[]) {
        // Mark that we're in a drag selection operation
        if (!this.dragSelectionStarted) {
            this.dragSelectionStarted = true;
            this.photosSelectedByDrag = [];
        }

        // Get the photos that should be selected (only actual photos, not placeholders)
        const validPhotosInRectangle = photosInRectangle
            .map(({ photo }) => photo)
            .filter(photo => !isPlaceholder(photo));

        // Remove photos that were selected by previous drag operations but are no longer in rectangle
        this.photosSelectedByDrag.forEach(photo => {
            if (!validPhotosInRectangle.some(p => isSame(p, photo))) {
                // This photo was selected by drag before but is no longer in rectangle - deselect it
                if (this.isSelected(photo)) {
                    this.photoSelectionChanged(photo, false);
                }
            }
        });

        // Add photos that are now in rectangle but weren't selected by drag before
        validPhotosInRectangle.forEach(photo => {
            if (!this.photosSelectedByDrag.some(p => isSame(p, photo))) {
                // This photo is new to the drag selection - select it
                if (!this.isSelected(photo)) {
                    this.photoSelectionChanged(photo, true);
                }
            }
        });

        // Update the list of photos selected by current drag operation
        this.photosSelectedByDrag = [...validPhotosInRectangle];
    }

    // Reset drag selection state when drag ends (called from DragSelection component)
    public resetDragSelection() {
        this.dragSelectionStarted = false;
        this.photosSelectedByDrag = [];
    }

    private async loadPhotos() {
        if (!this.id || this.sessionNotFound) return;
        const alreadyLoaded = photosState.photosLoaded.includes(this.id);
        this.photosLoading = !alreadyLoaded;
        this.photosLoadFailed = false;
        try {
            await photosState.loadSessionPhotosIfNotLoaded(this.id);
            if (alreadyLoaded) await photosState.refreshSessionSaleOptions(this.id);
        } catch (error) {
            this.photosLoadFailed = true;
        } finally {
            this.photosLoading = false;
            this.$nextTick(() => this.resizeListener && this.resizeListener(new Event('resize')));
        }
    }

    private onThumbSettled(index: number) {
        if (index >= 12 || index <= this.revealedThrough) return;
        this.readyThumbs.add(index);
        this.revealNextThumb();
    }

    private revealNextThumb() {
        if (this.revealTimer || !this.readyThumbs.has(this.revealedThrough + 1)) return;
        this.revealTimer = window.setTimeout(() => {
            this.revealTimer = undefined;
            this.readyThumbs.delete(this.revealedThrough + 1);
            this.revealedThrough += 1;
            this.revealNextThumb();
        }, this.revealedThrough < 0 ? 90 : 25);
    }

    public hideLightbox() {
        this.lightboxOpen = false;
        this.lockShortcuts = true;
        this.finishLightboxSlide(true);
        this.syncLightboxBodyClasses();
        setTimeout(() => this.lockShortcuts = false, 5);
        const photoRef = this.$refs[`photo${this.lightboxIndex}`] as any;
        const photoComponent = Array.isArray(photoRef)
            ? photoRef.find((candidate) => candidate && typeof candidate.blink === 'function')
            : photoRef;

        if (photoComponent && typeof photoComponent.blink === 'function') {
            this.$nextTick(() => photoComponent.blink());
        }
    }

    public onLightboxIndexChange(oldIndex: number, newIndex: number) {
        const direction = newIndex >= oldIndex ? 'next' : 'prev';
        const fromSrc = this.largeThumbs[oldIndex];
        const toSrc = this.largeThumbs[newIndex];

        this.lightboxIndex = newIndex;
        if (fromSrc && toSrc && fromSrc !== toSrc) {
            this.startLightboxSlide(direction, fromSrc, toSrc, newIndex);
        }
        this.$nextTick(() => this.syncLightboxBodyClasses());
    }

    get lightboxCurrentPhoto(): Photo | null {
        const photo = this.photos[this.lightboxIndex];
        return photo && isPhoto(photo) && !isPlaceholder(photo) ? photo : null;
    }

    get lightboxCurrentPhotoRecommended() {
        return !!this.lightboxCurrentPhoto?.recommended;
    }

    public async toggleLightboxRecommendation() {
        const photo = this.lightboxCurrentPhoto;
        if (!photo || this.lightboxRecommendationInProgress) {
            return;
        }

        const cooldownMs = 2000;
        const startedAt = Date.now();
        this.lightboxRecommendationInProgress = true;

        try {
            if (photo.recommended) {
                await photosState.unrecommend(photo.id);
            } else {
                await photosState.recommend(photo.id);
            }
            this.$nextTick(() => this.syncLightboxBodyClasses());
        } finally {
            const remaining = cooldownMs - (Date.now() - startedAt);
            if (remaining > 0) {
                await new Promise((resolve) => setTimeout(resolve, remaining));
            }
            this.lightboxRecommendationInProgress = false;
        }
    }

    private findPhotoIndex(p: Element) {
        return this.photos.findIndex((_p) => isSame(p, _p));
    }

    private isSelected(p: Element) {
        return this.selectedPhotos.some((_p) => isSame(p, _p));
    }

    private playPhotoSelectSound() {
        this.photoSelectSound.currentTime = 0;
        playInterfaceSound(this.photoSelectSound).catch(() => undefined);
    }

    private playPhotoDeselectSound() {
        this.photoDeselectSound.currentTime = 0;
        playInterfaceSound(this.photoDeselectSound, PHOTO_DESELECT_SOUND_VOLUME).catch(() => undefined);
    }

    private created() {
        this.photosLoading = !!this.id && !photosState.photosLoaded.includes(this.id);
    }

    private mounted() {
        this.loadPhotos();
        this.unsubscribeStoreAction = store.subscribeAction<{ type: string, payload: { id: string } }>((action) => {
            const { type } = action;
            if (type === 'overwritePhoto') {
                const { payload: { id }} = action;
                const originalPhoto = this.photos.filter(isPhoto).find(({ id: pId }) => pId === id);
                if (originalPhoto && !this.originalFilenames[id]) {
                    this.originalFilenames[id] = originalPhoto.filename;
                }
            }
        });
        this.resizeListener = () => {
            const justifiedRef = this.$refs.justifiedLayout as Vue | undefined;
            if (justifiedRef && (justifiedRef as any).$el) {
                this.justifiedLayoutWidth = (justifiedRef as any).$el.clientWidth;
            } else {
                const gallery = this.$el.querySelector('.session-photos-visibility') as HTMLElement | null;
                this.justifiedLayoutWidth = gallery?.clientWidth || Math.max(window.innerWidth - 48, 320);
            }
        };
        window.addEventListener(
            'resize',
            this.resizeListener,
        );
        this.lightboxWheelLockListener = (event: WheelEvent) => {
            if (!this.lightboxOpen) {
                return;
            }
            event.preventDefault();
        };
        document.addEventListener('wheel', this.lightboxWheelLockListener, { passive: false });
        (this.resizeListener as () => void)();
        window.setTimeout(() => {
            this.isSessionPhotosVisible = true;
        }, 20);

        this.shortcutListener = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement;
            if (
                this.lockShortcuts ||
                target.tagName.toLowerCase() === 'input' ||
                target.classList.contains('v-dialog__content') ||
                target.isContentEditable
            ) {
                return;
            }
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
                this.photos.forEach((item) => this.photoSelectionChanged(item, true));
                e.preventDefault();
            }

            if (e.key === 'Escape' && this.selectedPhotos.length > 0) {
                this.clearSelection();
                e.preventDefault();
            }
        };
        window.addEventListener('keydown', this.shortcutListener);
        groupOperationBus.$on('deletePhotos', this.onDeletePhotos);
        replaceBus.$on('requestConflictOverride', this.onRequestConflictOverride);
        replaceBus.$on('resolveConflictOverride', this.onResolveConflictOverride);
        this.syncLightboxBodyClasses();
    }

    private clearSelection() {
        this.selectedPhotos = [];
        this.shiftAnchor = null;
        this.playPhotoDeselectSound();
    }

    private destroyed() {
        if (this.revealTimer) window.clearTimeout(this.revealTimer);
        if (this.unsubscribeStoreAction) this.unsubscribeStoreAction();
        groupOperationBus.$off('deletePhotos', this.onDeletePhotos);
        replaceBus.$off('requestConflictOverride', this.onRequestConflictOverride);
        replaceBus.$off('resolveConflictOverride', this.onResolveConflictOverride);
        if (this.resizeListener) {
            window.removeEventListener('resize', this.resizeListener);
        }
        if (this.shortcutListener) {
            window.removeEventListener('keydown', this.shortcutListener);
        }
        if (this.lightboxWheelLockListener) {
            document.removeEventListener('wheel', this.lightboxWheelLockListener);
        }
        document.body.classList.remove('raws-lightbox-open');
        document.body.classList.remove('raws-lightbox-photo-recommended');
        this.finishLightboxSlide(true);
        sessionsState.setCurrentSession(null);
    }

    private startLightboxSlide(direction: 'next' | 'prev', fromSrc: string, toSrc: string, newIndex: number) {
        if (this.lightboxSlideTimeout) {
            window.clearTimeout(this.lightboxSlideTimeout);
            this.lightboxSlideTimeout = undefined;
        }

        const startedAt = Date.now();
        const slideToken = ++this.lightboxSlideToken;
        const nextPhoto = this.photos[newIndex];
        this.lightboxSlideDirection = direction;
        this.lightboxSlideFromSrc = fromSrc;
        this.lightboxSlideToSrc = toSrc;
        this.lightboxSlideToRecommended = !!(nextPhoto && isPhoto(nextPhoto) && nextPhoto.recommended);
        this.lightboxSlideReadyDeadline = startedAt + 900;
        this.lightboxSlideSettling = false;
        this.lightboxSlideFinalStyle = {};
        this.lightboxSlideAnimating = false;
        document.body.classList.remove('raws-lightbox-sliding');
        document.body.classList.remove('raws-lightbox-slide-settling');

        requestAnimationFrame(() => {
            if (slideToken !== this.lightboxSlideToken || !this.lightboxOpen) {
                return;
            }

            document.body.classList.add('raws-lightbox-sliding');
            this.lightboxSlideAnimating = true;

            const finishWhenReady = () => {
                if (slideToken !== this.lightboxSlideToken || !this.lightboxOpen) {
                    return;
                }
                const remaining = Math.max(300 - (Date.now() - startedAt), 0);
                this.lightboxSlideTimeout = window.setTimeout(
                    () => this.finishLightboxSlide(false, slideToken, toSrc),
                    remaining,
                );
            };

            const nextImage = new Image();
            nextImage.onload = finishWhenReady;
            nextImage.onerror = finishWhenReady;
            nextImage.src = toSrc;
        });
    }

    private finishLightboxSlide(immediate = false, slideToken = this.lightboxSlideToken, targetSrc = this.lightboxSlideToSrc) {
        if (this.lightboxSlideTimeout) {
            window.clearTimeout(this.lightboxSlideTimeout);
            this.lightboxSlideTimeout = undefined;
        }

        if (immediate) {
            this.lightboxSlideToken++;
            document.body.classList.remove('raws-lightbox-sliding');
            document.body.classList.remove('raws-lightbox-slide-settling');
            this.lightboxSlideSettling = false;
            this.lightboxSlideFinalStyle = {};
            this.lightboxSlideAnimating = false;
            return;
        }

        if (slideToken !== this.lightboxSlideToken || !this.lightboxOpen) {
            return;
        }

        if (!this.isNativeLightboxImageReady(targetSrc) && Date.now() < this.lightboxSlideReadyDeadline) {
            this.lightboxSlideTimeout = window.setTimeout(
                () => this.finishLightboxSlide(false, slideToken, targetSrc),
                35,
            );
            return;
        }

        this.lightboxSlideFinalStyle = this.getNativeLightboxImageStyle(targetSrc);

        requestAnimationFrame(() => {
            if (slideToken !== this.lightboxSlideToken) {
                return;
            }

            document.body.classList.remove('raws-lightbox-sliding');
            document.body.classList.add('raws-lightbox-slide-settling');
            this.lightboxSlideSettling = true;
        });

        this.lightboxSlideTimeout = window.setTimeout(() => {
                if (slideToken !== this.lightboxSlideToken) {
                    return;
                }
                document.body.classList.remove('raws-lightbox-slide-settling');
                this.lightboxSlideSettling = false;
                this.lightboxSlideFinalStyle = {};
                this.lightboxSlideAnimating = false;
        }, 340);
    }

    private isNativeLightboxImageReady(src: string) {
        if (!src) {
            return true;
        }

        const expectedSrc = this.resolveBrowserImageSrc(src);
        const images = Array.from(document.querySelectorAll<HTMLImageElement>('.vel-img'));

        return images.some((img) => {
            const renderedSrc = img.currentSrc || img.src;
            return renderedSrc === expectedSrc && img.complete && img.naturalWidth > 0;
        });
    }

    private resolveBrowserImageSrc(src: string) {
        const image = new Image();
        image.src = src;
        return image.src;
    }

    private getNativeLightboxImageStyle(src: string): { [property: string]: string } {
        const image = this.findNativeLightboxImage(src);

        if (!image) {
            return {};
        }

        const rect = image.getBoundingClientRect();
        return {
            top: `${rect.top + rect.height / 2}px`,
            left: `${rect.left + rect.width / 2}px`,
            width: `${rect.width}px`,
            height: `${rect.height}px`,
            maxWidth: 'none',
            maxHeight: 'none',
            transform: 'translate(-50%, -50%) scale(1)',
        };
    }

    private findNativeLightboxImage(src: string) {
        const expectedSrc = this.resolveBrowserImageSrc(src);
        const images = Array.from(document.querySelectorAll<HTMLImageElement>('.vel-img'));

        return images.find((img) => (img.currentSrc || img.src) === expectedSrc) || null;
    }

    @Watch('lightboxOpen')
    @Watch('lightboxIndex')
    @Watch('allPhotos', { deep: true })
    private syncLightboxBodyClasses() {
        document.body.classList.toggle(
            'raws-lightbox-open',
            this.lightboxOpen,
        );
        document.body.classList.toggle(
            'raws-lightbox-photo-recommended',
            this.lightboxOpen && this.lightboxCurrentPhotoRecommended,
        );
    }

    @Debounce(1500)
    @Watch('sessionData', { deep: true })
    private async onSessionDataChange(value: SessionData) {
        if (!value.name) {
            return;
        }

        if (!this.id && value.name) {
            await sessionsState.saveSession({ name: value.name, userId: userState.user!.id });

            if (sessionsState.lastAddedSession) {
                this.$router.push({
                    name: 'editSession',
                    params: {
                        id: sessionsState.lastAddedSession.id,
                    },
                });
            } else {
                throw new Error('Error saving session');
            }
        } else if (
            this.id &&
            JSON.stringify(value) !== JSON.stringify(this.session!.data) //JSON object comparison
        ) {
            await sessionsState.updateSession(new SessionModel(this.id, value.name, value.watermark));
        }
    }

    @Watch('session', { immediate: true })
    private async onSessionChange(value: SessionModel) {
        if (value) {
            this.sessionData = value.data;
            sessionsState.setCurrentSession(value);
        } else {
            this.sessionData = clone(initialSessionData);
            sessionsState.setCurrentSession(null);
        }
    }

    @Watch('shiftAnchor')
    private clearLastShiftSelection() {
        this.lastShiftSelection = [];
    }

    private playSpinSound() {
        const audio = createInterfaceSound('/sound/spin.wav');
        playInterfaceSound(audio).catch(error => {
            console.log('Audio playback failed:', error);
        });
    }
}
